import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  LogIn, 
  LogOut, 
  RefreshCw, 
  UserPlus, 
  UserMinus,
  Search,
  Download,
  Filter,
  X,
  Key
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Action type configurations
const ACTION_CONFIG = {
  'login': { 
    icon: LogIn, 
    label: 'Giriş', 
    color: 'bg-green-100 text-green-800 border-green-200' 
  },
  'logout': { 
    icon: LogOut, 
    label: 'Çıkış', 
    color: 'bg-gray-100 text-gray-800 border-gray-200' 
  },
  'token_refreshed': { 
    icon: RefreshCw, 
    label: 'Token Yenilendi', 
    color: 'bg-brand-neon-soft text-brand-ink border-brand-line' 
  },
  'token_revoked': { 
    icon: X, 
    label: 'Token İptal', 
    color: 'bg-brand-yellow text-brand-ink border-brand-ink/20' 
  },
  'user_signedup': { 
    icon: UserPlus, 
    label: 'Kayıt', 
    color: 'bg-brand-yellow-soft text-brand-ink border-brand-line' 
  },
  'user_deleted': { 
    icon: UserMinus, 
    label: 'Silindi', 
    color: 'bg-red-100 text-red-800 border-red-200' 
  },
  'user_recovery_requested': { 
    icon: Key, 
    label: 'Şifre Sıfırlama', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200' 
  }
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
};

// Format date for input
const formatDateForInput = (date) => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};

export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [setupRequired, setSetupRequired] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('limit', '500');
      
      if (emailFilter) {
        params.append('email_filter', emailFilter);
      }
      if (actionFilter && actionFilter !== 'all') {
        params.append('action_filter', actionFilter);
      }
      if (startDate) {
        params.append('start_date', `${startDate}T00:00:00Z`);
      }
      if (endDate) {
        params.append('end_date', `${endDate}T23:59:59Z`);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/audit-logs?${params}`);
      const data = await response.json();
      
      if (data.setup_required) {
        setSetupRequired(true);
        setLogs([]);
      } else if (data.logs) {
        setLogs(data.logs);
        setSetupRequired(false);
      } else {
        setLogs([]);
      }
      
      if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.message);
      toast.error('Giriş geçmişi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleSearch = () => {
    fetchLogs();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchLogs();
    }
  };

  const clearFilters = () => {
    setEmailFilter('');
    setActionFilter('all');
    setStartDate('');
    setEndDate('');
    setTimeout(() => fetchLogs(), 100);
  };

  const hasActiveFilters = emailFilter || actionFilter !== 'all' || startDate || endDate;

  // Export to Excel
  const exportToExcel = () => {
    if (logs.length === 0) {
      toast.error('Dışa aktarılacak veri yok');
      return;
    }

    setExporting(true);

    try {
      const BOM = '\uFEFF';
      const headers = ['Tarih', 'E-posta', 'İşlem', 'IP Adresi'];
      
      const rows = logs.map(log => {
        const actionConfig = ACTION_CONFIG[log.action] || { label: log.action };
        return [
          formatDate(log.created_at),
          log.email || log.username || '-',
          actionConfig.label,
          log.ip_address || '-'
        ];
      });

      const csvContent = BOM + [
        headers.join(';'),
        ...rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(';'))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const now = new Date();
      const fileName = `giris_gecmisi_${now.toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`${logs.length} kayıt dışa aktarıldı`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Dışa aktarma sırasında hata oluştu');
    } finally {
      setExporting(false);
    }
  };

  const getActionBadge = (action) => {
    const config = ACTION_CONFIG[action] || { 
      icon: LogIn, 
      label: action || '-', 
      color: 'bg-gray-100 text-gray-800 border-gray-200' 
    };
    const Icon = config.icon;
    
    return (
      <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-audit-logs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Giriş Geçmişi</h1>
          <p className="text-gray-600 text-sm mt-1">
            Kullanıcı oturum işlemlerini görüntüleyin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={exportToExcel} 
            disabled={exporting || logs.length === 0}
            variant="outline"
            data-testid="export-logs-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'İndiriliyor...' : 'Excel İndir'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtreler
            </CardTitle>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Temizle
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Email Filter */}
            <div>
              <Label className="text-xs">E-posta</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="E-posta ara..."
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pl-8 h-9"
                  data-testid="email-filter"
                />
              </div>
            </div>

            {/* Action Filter */}
            <div>
              <Label className="text-xs">İşlem Türü</Label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="h-9 mt-1" data-testid="action-filter">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="login">Giriş</SelectItem>
                  <SelectItem value="logout">Çıkış</SelectItem>
                  <SelectItem value="token_refreshed">Token Yenilendi</SelectItem>
                  <SelectItem value="token_revoked">Token İptal</SelectItem>
                  <SelectItem value="user_signedup">Kayıt</SelectItem>
                  <SelectItem value="user_recovery_requested">Şifre Sıfırlama</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <Label className="text-xs">Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9 mt-1"
                data-testid="start-date-filter"
              />
            </div>

            {/* End Date */}
            <div>
              <Label className="text-xs">Bitiş Tarihi</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9 mt-1"
                data-testid="end-date-filter"
              />
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full h-9" data-testid="search-btn">
                <Search className="h-4 w-4 mr-2" />
                Ara
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Sonuçlar
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({logs.length} kayıt)
              </span>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : setupRequired ? (
            <div className="text-center py-8 text-gray-500">
              <p>Audit log fonksiyonu kurulumu gerekiyor.</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Kayıt bulunamadı</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>E-posta</TableHead>
                    <TableHead>İşlem</TableHead>
                    <TableHead>IP Adresi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log, index) => (
                    <TableRow key={log.id || index} data-testid={`log-row-${index}`}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {log.email || log.username || '-'}
                      </TableCell>
                      <TableCell>
                        {getActionBadge(log.action)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {log.ip_address || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
